// Auth context for the CMS admin panel.
// Uses Emergent Google Auth. The session token is returned from /auth/session and
// also stored in localStorage to support cross-origin API calls when the platform
// ingress sets `Access-Control-Allow-Origin: *` (which precludes cookie credentials).
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setStoredToken, getStoredToken } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  login: () => {},
});

const ROLE_LEVEL = { viewer: 1, editor: 2, admin: 3, super_admin: 4 };

export function getGoogleOAuthRedirectUri() {
  const configuredUri = process.env.REACT_APP_OAUTH_REDIRECT_URI?.trim();
  if (configuredUri) return configuredUri;

  if (typeof window === "undefined") return "";

  const publicPath = (process.env.PUBLIC_URL || "").trim().replace(/^\/+|\/+$/g, "");
  const callbackPath = publicPath ? `/${publicPath}/admin/callback` : "/admin/callback";
  return new URL(callbackPath, `${window.location.origin}/`).toString();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    if (!getStoredToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setStoredToken(null);
    setUser(null);
  }

  function login() {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUrl = getGoogleOAuthRedirectUri();
    const googleAuthUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    googleAuthUrl.searchParams.set("client_id", CLIENT_ID);
    googleAuthUrl.searchParams.set("redirect_uri", redirectUrl);
    googleAuthUrl.searchParams.set("response_type", "id_token token"); // Returns id_token directly to client
    googleAuthUrl.searchParams.set("scope", "openid email profile");
    googleAuthUrl.searchParams.set("nonce", Math.random().toString(36).substring(2)); // Prevents replay attacks

    // Redirect user to Google Sign-In
    window.location.href = googleAuthUrl.toString();
  }

  return (
    <AuthContext.Provider value={{ user, loading, refresh: checkAuth, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasRole(user, min) {
  if (!user || !(user.role in ROLE_LEVEL) || !(min in ROLE_LEVEL)) return false;
  return ROLE_LEVEL[user.role] >= ROLE_LEVEL[min];
}
